/**
 * Each signed-in area's navigation: the paths `app/routes.tsx` registers,
 * and the label keys (`areas.<area>.nav.<key>`). Icons are attached in
 * `AreaPage`, inside the area chunks, so the entry carries only paths.
 * Most destinations are placeholders until the steps that build them.
 */

/** A navigation entry: its path and its label key in `areas.<area>.nav`. */
export interface AreaNavEntry {
  to: string;
  key: string;
  /** Mark it current only on an exact path match (the area's overview). */
  exact?: boolean;
  /** Shown only while this feature switch is on (`Me.features`). */
  feature?: string;
  /**
   * The family it is listed under, with a heading (`areas.<area>.sections.<key>`),
   * for an area with many destinations (the admin area).
   */
  section?: string;
  /**
   * Shown only to a member whose permissions in the acting school include
   * it, once they are known (the school area; `MyOrgs.permissions`).
   */
  permission?: string;
}

/** The signed-in areas. `account` (settings and consent) is open to every signed-in account. */
export type AppArea = 'candidate' | 'school' | 'admin' | 'account';

/**
 * The candidate profile's pages (`/candidate/profile/<section>`), in the
 * order they appear in the side navigation, in groups. A section is a
 * `SectionName` of the profile.
 */
export const PROFILE_NAV = [
  { group: 'you', sections: ['about', 'personal', 'household'] },
  {
    group: 'teaching',
    sections: ['teaching', 'licences', 'qualifications', 'experience', 'development', 'languages'],
  },
  { group: 'ahead', sections: ['preferences'] },
  { group: 'private', sections: ['confidential'] },
] as const;

/** Every section page, in order. */
export const PROFILE_SECTION_ORDER: readonly string[] = PROFILE_NAV.flatMap(
  (entry) => entry.sections,
);

/** A profile page's path. */
export const profilePath = (section: string) => `/candidate/profile/${section}`;

/** Navigation per area. */
export const AREA_NAV: Record<AppArea, readonly AreaNavEntry[]> = {
  candidate: [
    { to: '/candidate', key: 'overview', exact: true },
    { to: '/candidate/profile', key: 'profile' },
    { to: '/candidate/portfolio', key: 'portfolio' },
    { to: '/candidate/documents', key: 'documents' },
    { to: '/candidate/readiness', key: 'readiness', feature: 'readiness.checklist' },
    { to: '/candidate/references', key: 'references' },
    { to: '/candidate/share', key: 'share' },
    { to: '/candidate/qr', key: 'qr' },
    { to: '/candidate/requests', key: 'requests' },
    { to: '/candidate/views', key: 'views' },
    { to: '/candidate/jobs', key: 'jobs' },
    { to: '/candidate/saved-jobs', key: 'savedJobs' },
    { to: '/candidate/applications', key: 'applications' },
    { to: '/candidate/messages', key: 'messages' },
  ],
  school: [
    { to: '/school', key: 'overview', exact: true },
    { to: '/school/verification', key: 'verification', permission: 'org.profile.manage' },
    { to: '/school/team', key: 'team', permission: 'org.view' },
    { to: '/school/activity', key: 'activity', permission: 'org.activity.view' },
    { to: '/school/vacancies', key: 'vacancies', permission: 'org.view' },
    { to: '/school/pipelines', key: 'pipelines', permission: 'org.vacancy.manage' },
    { to: '/school/interviews', key: 'interviews', permission: 'org.interviews.score' },
    { to: '/school/scorecards', key: 'scorecards', permission: 'org.applications.manage' },
    { to: '/school/candidates', key: 'candidates' },
    { to: '/school/shortlists', key: 'shortlists' },
    { to: '/school/messages', key: 'messages', permission: 'org.view' },
  ],
  admin: [
    { to: '/admin', key: 'overview', exact: true },
    { to: '/admin/reviews', key: 'reviews', section: 'work' },
    { to: '/admin/support', key: 'support', section: 'work' },
    { to: '/admin/messages', key: 'messages', section: 'work' },
    { to: '/admin/accounts', key: 'accounts', section: 'people' },
    { to: '/admin/schools', key: 'schools', section: 'people' },
    { to: '/admin/system', key: 'system', section: 'system' },
    { to: '/admin/logs', key: 'logs', section: 'system' },
    { to: '/admin/activity', key: 'activity', section: 'system' },
    { to: '/admin/integrity', key: 'integrity', section: 'system' },
    { to: '/admin/features', key: 'features', section: 'settings' },
    { to: '/admin/automation', key: 'automation', section: 'settings' },
    { to: '/admin/templates', key: 'templates', section: 'settings' },
    { to: '/admin/channels', key: 'channels', section: 'settings' },
    { to: '/admin/ai', key: 'ai', section: 'settings' },
    { to: '/admin/requirements', key: 'requirements', section: 'settings' },
    { to: '/admin/taxonomy', key: 'taxonomy', section: 'settings' },
    { to: '/admin/play', key: 'play', section: 'settings' },
    { to: '/admin/tools', key: 'tools', section: 'settings' },
  ],
  account: [
    { to: '/account', key: 'settings', exact: true },
    { to: '/account/contact', key: 'contact' },
    { to: '/account/security', key: 'security' },
    { to: '/account/privacy', key: 'privacy' },
    { to: '/account/notifications', key: 'notifications' },
  ],
};

/**
 * Pages of each area that are not in its navigation (detail pages with
 * route parameters). Each is shown under the navigation entry its path
 * starts with.
 */
export const AREA_PAGES: Record<AppArea, readonly string[]> = {
  // A school as teachers see it (H4.5); a job on the board (H5.1).
  candidate: [
    // The teacher's CV, as schools see it (2026-11-03).
    '/candidate/profile/cv',
    '/candidate/profile/$section',
    '/candidate/cv',
    '/candidate/cv/update',
    '/schools/$orgId',
    '/candidate/jobs/$jobId',
    // Gradex Tools (ADR 0042), an area of its own with its own frame (ADR 0050).
    '/candidate/tools',
    '/candidate/tools/whiteboard',
    '/candidate/tools/classes',
    // A class's seating plan (2026-11-02).
    '/candidate/tools/classes/$classId/seating',
    '/candidate/tools/quizzes',
    // Create a quiz with AI or a spreadsheet (QUIZ.md Q3).
    '/candidate/tools/quizzes/create',
    '/candidate/tools/quizzes/$quizId',
    '/candidate/tools/runs/$runId',
    // The teacher's remote for a live game, on their phone (QUIZ.md Q1.2).
    '/candidate/tools/runs/$runId/remote',
  ],
  school: [
    '/school/candidates/$profileId',
    '/school/vacancies/$vacancyId',
    '/school/vacancies/$vacancyId/pipeline',
    '/school/applications/$applicationId',
  ],
  admin: [
    '/admin/automation/outbox/$messageId',
    '/admin/accounts/$accountId',
    '/admin/history/$entityType/$entityId',
    '/admin/reviews/$taskId',
    '/admin/document-checks/$taskId',
    '/admin/references/$taskId',
    '/admin/schools/$orgId',
    '/admin/org-verifications/$taskId',
    '/admin/templates/$code',
    '/admin/messages/$conversationId',
    '/admin/message-holds/$taskId',
    '/admin/support/$threadId',
  ],
  account: ['/account/consent', '/account/start', '/account/join/$invitationId'],
};
